// src/com/file_of_report/TransPlanResultReportInput.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, DialogContent, DialogActions, CircularProgress, Grid, Box } from '@mui/material';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CloseIcon from '@mui/icons-material/Close';
import { AppWord, AppObject, AppStyle, CodeGroup } from '../../value/AppConst';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useAlertSetter } from '../../api/app-hook';
import DraggableEditor from '../../common/DraggableEditor';
import DateRangePicker from '../../common/DateRangePicker';
import InputUtil from '../../value/InputUtil';
import AutoPopTextField from '../../common/AutoPopTextField';
import VzmSelector from '../../common/VzmSelector';
import {
    codeTreeRepo, comInfoListRepo, storeInfoListRepo, storeInfoListUsingRepo,
    userInfoRepo, vehInfoListOfDcRepo, vehTypeGroupOrderListByDcRepo, waveListRepo
} from '../../value/AppModel';
import { LIST_USING_STORE_INFO } from '../store_info/StoreInfoGql';
import { TransPlanResultReportDict } from './ReportDict';
import { devideVehiclesByCompany, LIST_VEH_INFO_OF_DC } from '../veh_info/VehInfoGql';
import { LIST_VEH_TYPE_GROUP_ORDER_BY_DC } from '../veh_type_group_order/VehTypeGroupOrderGql';
import { LIST_COM_INFO } from '../com_info/ComInfoGql';
import ValueMisc from '../../value/ValueMisc';
import { CREATE_TRANS_PLAN_RESULT_REPORT } from './TransPlanResultReportGql';
import { GET_FILE_CREATE_HIS } from '../file_create_his/FileCreateHisGql';

const MAX_ATTEMPTS = 10; // 레포트 생성 확인을 위한 조회 최대 시도 횟수
const POLLING_INTERVAL = 3000; // 3초 간격으로 레포트 생성 확인 조회

const InputMap = TransPlanResultReportDict;
const EmptyVehInfoList = [{vehPlates:AppWord.ChooseKorean, driverName:'', driverMobile:''}];

const initialData = {...AppObject.emptyInputData,};
initialData.data.dateFrom = ValueMisc.getYMD(new Date());
initialData.data.dateTill = ValueMisc.getYMD(new Date());

/**
 * 운송실적 리포트 생성을 위한 입력 팝업 후 서버에 생성 요청.
 * 생성이 잘 되면 해당 데이터를 주기적으로 불러서 확인한 후
 * 상위 컴포넌트(FileCreateHisTransportReport)에서 목록을 갱신하게 함.
 * 확인 방법: file_down_up가 'DOWN' - OK. 'FAIED' - 오류, 'UP' - 생성 중
 * @param {*} param0 
 * @returns 
 */
export default function TransPlanResultReportInput({
    open,
    onClose,
    onComplete,
}) {
    const codeTree = useReactiveVar(codeTreeRepo);
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [attemtCount, setAttemptCount] = useState(0);
    const [message, setMessage] = useState('');
    const [input, setInput] = useState({...initialData});
    const waveList = useReactiveVar(waveListRepo);
    const storeInfoListData = useReactiveVar(storeInfoListRepo);
    const storeInfoListUsingData = useReactiveVar(storeInfoListUsingRepo);
    // const [storeName, setStoreName] = useState(''); // only for view.
    const vehInfoListOfDcData = useReactiveVar(vehInfoListOfDcRepo);
    const [vehInfoList, setVehInfoList] = useState(EmptyVehInfoList);
    const vehTypeGroupOrderListByDc = useReactiveVar(vehTypeGroupOrderListByDcRepo);
    const vehTypeList = useMemo(()=>{
        if(vehTypeGroupOrderListByDc.records.length > 0) {
            const list = [...vehTypeGroupOrderListByDc.records];
            list.unshift({vehTypeId:AppWord.ChooseValue, vehTypeName:AppWord.ChooseKorean});
            return list;
        }
        return [{vehTypeId:AppWord.ChooseValue, vehTypeName:AppWord.ChooseKorean}];
    }, [vehTypeGroupOrderListByDc]);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const comInfoList = useMemo(()=>{
        if(comInfoListData.records.length > 0) {
            const list = comInfoListData.records.filter(com=>com.dcCd===sessionInfo.user.dscIdAs && com.useYn==='Y') //[...comInfoListData.records];
            list.unshift({comId:AppWord.ChooseValue, comName:AppWord.ChooseKorean});
            return list;
        }
        return [{comId:AppWord.ChooseValue, comName:AppWord.ChooseKorean}];
    }, [comInfoListData]);
    const addAlert = useAlertSetter();
    const loadRef = useRef(false);
    const attemptCountRef = useRef(0);
    const timeoutRef = useRef(null);

    // ##### Call GraphQL to get List #####
    const [getVehTypeGroupOrderList, responseVehTypeList] = useLazyQuery(LIST_VEH_TYPE_GROUP_ORDER_BY_DC, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetVehTypeList(data, option)},
        onError: (error) => addAlert({title: "차종목록 조회 오류", message: [error.message]})
    });
    const [getComInfoList, responseComList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComList(data, option)},
        onError: (error) => addAlert({title: '운송사 목록 조회 오류', message: [error.message]})
    });
    const [getVehInfoListOfDc, responseVehInfoList] = useLazyQuery(LIST_VEH_INFO_OF_DC, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetVehInfoList(data, option)},
        onError: (error) => addAlert({title: "차량목록 조회 오류", message: [error.message]})
    });
    // use_yn=='Y'인 점포목록을 조회 {storeId, storeTypeCd, storeTypeName, storeName, useYn}
    const [getStoreInfoListUsing, responseStoreList] = useLazyQuery(LIST_USING_STORE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetStoreList(data, option)},
        onError: (error) => addAlert({title: "점포목록 조회 오류", message: [error.message]})
    });

    const [createReport, responseCreateReport] = useMutation( CREATE_TRANS_PLAN_RESULT_REPORT, {
        onCompleted: (data, option) => onCompleteCreateReport(data, option),
        onError: (error) => addAlert({title: '운송실적 리포트 생성 오류', message: [error.message]})
    });
    // 레포트 생성 후 일정 시간 간격으로 조회해서 생성 완료 여부 확인.
    const [getFileCreateHisItem, responseGetItem] = useLazyQuery(GET_FILE_CREATE_HIS, {
            ...AppObject.NoCachedFetch,
            onCompleted: (data, option) => {onCompleteGetItem(data, option)},
            onError: (error) => addAlert({title: "레포트 생성 확인 오류", message: [error.message]})
    });
    
    const getStoreInfoListUsingOnCondition = () => {
        const now = new Date().getTime() / 1000;
        if(now - storeInfoListUsingData.when < 300 && storeInfoListUsingData.records.length > 0) return; // 5분 이내 조회된 데이터는 재사용.
        // 큰 데이터에 있으면 복사해 오기.
        if(now - storeInfoListData.when < 300 && storeInfoListData.records.length > 0) {
            storeInfoListUsingRepo({
                when: storeInfoListData.when,
                records: storeInfoListData.records.filter((rec)=>{
                    return rec.useYn === 'Y';
                }).map((rec)=>{
                    const newRec = {
                        storeId: rec.storeId,
                        storeTypeCd: rec.storeTypeCd,
                        storeTypeName: rec.storeTypeName,
                        storeName: rec.storeName,
                        useYn: rec.useYn
                    };
                    return newRec;
                })
            });
            return;
        }
        getStoreInfoListUsing();
    };

    useEffect(() => {
        if(sessionInfo.isTransCompanyUser()) {
            if(input.data.comId !== sessionInfo.user.dscIdAs) {
                const newInput = {...input};
                newInput.data.comId = sessionInfo.user.dscIdAs;
                setInput(newInput);
            }
        }
    }, [input, sessionInfo]);
    
    useEffect(() => {
        if(input.data.comId && vehInfoListOfDcData.records[input.data.comId]) {
            setVehInfoList(vehInfoListOfDcData.records[input.data.comId]);
        }
        else setVehInfoList(EmptyVehInfoList);
    }, [vehInfoListOfDcData, input]);

    useEffect(() => {
        if(open) {
            if(!loadRef.current) {
                // 점포목록
                getStoreInfoListUsingOnCondition();
                const now = new Date().getTime() / 1000;

                // 차종목록
                let diff = now - vehTypeGroupOrderListByDc.when;
                if(diff > 300) {
                    getVehTypeGroupOrderList({variables:{dcCd:sessionInfo.getDcCd()}});
                }

                // 운송사목록
                if(!sessionInfo.isTransCompanyUser()) {
                    diff = now - comInfoListData.when;
                    if(diff > 300) {
                        getComInfoList();
                    }
                }

                // 차량목록
                diff = now - vehInfoListOfDcData.when;
                if(diff > 300) {
                    getVehInfoListOfDc({variables:{dcCd:sessionInfo.getDcCd()}});
                }

                // 배차구분 코드목록 만들기
                const waveCodes = ValueMisc.getCodes(codeTree, CodeGroup.TRANS_TYPE_CD);
                const list = [{transTypeCd: AppWord.ChooseValue, label: AppWord.ChooseKorean}];
                waveCodes.forEach(item => {
                    list.push({transTypeCd: item.codeId, label: item.codeName});
                });
                waveListRepo(list);

                setInput({...initialData});
                setMessage('레포트 생성을 위한 기초정보를 설정 후 생성 버튼을 눌러주세요.');
                loadRef.current = true;
                setAttemptCount(0);
                attemptCountRef.current = 0;
            }
        }
        else {
            setInput({...initialData});
            setMessage('');
            loadRef.current = false;
            setAttemptCount(0);
            attemptCountRef.current = 0;
        }
    }, [open]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetStoreList = (data, clientOption) => {
        if(data.storeInfoListUsing) {
            storeInfoListUsingRepo({when:new Date().getTime()/1000, records:data.storeInfoListUsing});
        }
    };
        
    const onCompleteGetComList = (data, option) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };

    const onCompleteGetVehTypeList = (data, clientOption) => {
        if(data.vehTypeGroupOrderListByDc) {
            vehTypeGroupOrderListByDcRepo({when:new Date().getTime()/1000, records:data.vehTypeGroupOrderListByDc});
        }
    };

    const onCompleteGetVehInfoList = (data, clientOption) => {
        if(data.vehInfoListOfDc) {
            // devideVehiclesByCompany : 맨 앞에 "선택" 넣고, 차량번호에 '신규'가 들어간 것은 맨 뒤로 보내기.
            vehInfoListOfDcRepo({
                when:new Date().getTime()/1000,
                records:devideVehiclesByCompany(data.vehInfoListOfDc, ValueMisc.vehicleSorter)
            });
        }
    };

    const repeatCheckReportCreation = (fileCreateHisSeq) => {
        if(attemptCountRef.current >= MAX_ATTEMPTS) {
            addAlert({
                title: "레포트 생성 확인 실패",
                message: ['레포트 생성 확인을 반복했으나 아직 확인할 수 없습니다. 잠시 후 목록을 다시 조회해서 확인해 주세요.']});
            return;
        }

        attemptCountRef.current += 1;
        setAttemptCount(attemptCountRef.current);
        getFileCreateHisItem({variables:{fileCreateHisSeq:fileCreateHisSeq}});
    };

    const onCompleteGetItem = (data, clientOption) => {
        if(data.fileCreateHisItem) {
            // Check fileDownUp==='DOWN' or try again after some delay
            if(data.fileCreateHisItem.fileDownUp === 'DOWN') {
                // 레포트 생성 완료
                setAttemptCount(0);
                onComplete();
            }
            else if(data.fileCreateHisItem.fileDownUp === 'UP') {
                setMessage(`레포트 생성 중입니다. ${POLLING_INTERVAL/1000}초 후 다시 확인합니다. (${MAX_ATTEMPTS - attemptCountRef.current}회)`);
                timeoutRef.current = setTimeout(() => {
                    repeatCheckReportCreation(data.fileCreateHisItem.fileCreateHisSeq);
                }, POLLING_INTERVAL);
            }
            else {
                // 기타 상태 - 오류로 간주 (FAILED)
                setAttemptCount(0);
                addAlert({
                    title: "레포트 생성 오류",
                    message: ['레포트 생성 중 알 수 없는 상태가 발생했습니다. 관리자에게 문의해 주세요.']});
            }
        }
        else {
            addAlert({
                title: "레포트 생성 확인 오류",
                message: ['레포트 생성 내역을 확인하는 중 알 수 없는 오류가 발생했습니다. 관리자에게 문의해 주세요.']});
        }
    };

    const onCompleteCreateReport = (data, clientOption) => {
        if(data.transPlanResultReportAdd.ok) {
            setMessage('리포트 생성 요청이 정상적으로 접수되었습니다. 결과를 확인 중입니다.');
            attemptCountRef.current = 0;
            repeatCheckReportCreation(data.transPlanResultReportAdd.item.fileCreateHisSeq);
        }
        else {
            setMessage('리포트 생성 오류가 발생했습니다.');
            addAlert({title: '운송실적 리포트 생성 오류', message: [data.transPlanResultReportAdd.message]});
        }
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, false);
        if(id === InputMap.dateFrom.id && input.data.dateTill < value) {
            newInput.data.dateTill = value;
        }
        else if(id === InputMap.dateTill.id && input.data.dateFrom > value) {
            newInput.data.dateFrom = value;
        }
        setInput(newInput);
    };
    
    const onChangeSelect = (id, value) => {
        onChangeValue(id, value === AppWord.ChooseValue ? undefined : value, false);
    };
    
    const onChangeFrom = (ymd) => {
        onChangeValue(InputMap.dateFrom.id, ymd, false);
    };

    const onChangeTo = (ymd) => {
        onChangeValue(InputMap.dateTill.id, ymd, false);
    };

    const onClickCreate = () => {
        const param = {
            dateFrom: input.data.dateFrom,
            dateTill: input.data.dateTill,
            dcCd: sessionInfo.getDcCd(), // 넣지 않아도 되지만..
        };
        if(input.data.transTypeCd) param.transTypeCd = input.data.transTypeCd;
        if(input.data.vehTypeId) param.vehTypeId = input.data.vehTypeId;
        if(input.data.storeId) param.storeId = input.data.storeId;
        if(sessionInfo.isTransCompanyUser()) {
            param.comId = sessionInfo.user.dscId; // 넣지 않아도 되지만
            if(input.data.vehPlates) param.vehPlates = input.data.vehPlates;
        }
        else if(input.data.comId) {
            param.comId = input.data.comId;
            if(input.data.vehPlates) param.vehPlates = input.data.vehPlates;
        }
        setMessage('리포트 생성을 요청하는 중입니다.');
        createReport({variables:{transPlanResultReport: param}}); // 백엔드에서 세션에 따라 분기.(Service단)
    };

    const loadingAny
    = responseStoreList.loading || responseVehInfoList.loading
    || responseVehTypeList.loading || responseComList.loading
    || responseCreateReport.loading || responseGetItem.loading;

    return(
        <DraggableEditor open={open} maxWidth='sm'
            startIcon={<NewspaperIcon fontSize={AppWord.LARGE} style={{color:'white', marginRight:15}} />}
            title={'운송실적 리포트 생성'}
        >
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <DateRangePicker
                            dateFrom={input.data.dateFrom}
                            dateTo={input.data.dateTill}
                            onChangeFrom={onChangeFrom}
                            onChangeTo={onChangeTo}
                        />
                        <div style={{margin:10, fontSize:'0.9rem', color:'#666'}}>
                            뒷쪽 날짜부터 맞추면 편리합니다.
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <VzmSelector
                            dict={InputMap.transTypeCd}
                            value={input.data.transTypeCd || AppWord.ChooseValue}
                            onChange={onChangeSelect}
                            list={waveList}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <VzmSelector
                            dict={InputMap.vehTypeId}
                            value={input.data.vehTypeId || AppWord.ChooseValue}
                            onChange={onChangeSelect}
                            list={vehTypeList}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutoPopTextField
                            dict={InputMap.storeId}
                            value={input.data.storeId}
                            onChange={onChangeValue}
                            list={storeInfoListUsingData.records}
                            nullable={true}
                            fullWidth={true}
                        />
                    </Grid>
                    {
                        !sessionInfo.isTransCompanyUser() &&
                        <Grid item xs={12}>
                            <VzmSelector
                                dict={InputMap.comId}
                                value={input.data.comId}
                                onChange={onChangeValue}
                                list={comInfoList}
                                fullWidth={true}
                            />
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <AutoPopTextField
                            dict={InputMap.vehPlates}
                            value={input.data.vehPlates}
                            onChange={onChangeValue}
                            disabled={!Boolean(input.data.comId)}
                            fullWidth={true}
                            nullable={true}
                            list={vehInfoList}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box
                            style={{
                                fontSize: '0.9rem',
                                border: '1px solid #ccc',
                                padding: 12,
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {
                                attemtCount > 0 &&
                                <CircularProgress size={20} style={{marginRight:10}} />
                            }
                            {
                                loadingAny && !responseCreateReport.loading ?
                                '기초데이터를 불러오는 중입니다. 잠시만 기다려주세요...' :
                                message
                            }
                        </Box>
                    </Grid>
                </Grid>

            </DialogContent>
            <DialogActions>
                {
                    loadingAny &&
                    <CircularProgress size={24} style={{marginRight:15}} />
                }
                <Button
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.primary}
                    startIcon={<NoteAddIcon />}
                    onClick={onClickCreate}
                    disabled={loadingAny}
                >생성</Button>
                <Button
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.warning}
                    startIcon={<CloseIcon />}
                    onClick={onClose}
                >취소</Button>
            </DialogActions>
        </DraggableEditor>
    )
}
